import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, ArrowRight, Sparkles, Loader2, Check, Save, Rocket, Eye, ClipboardList, ShoppingCart,
  MessageCircle, Phone, Calendar, Globe, Upload, Wand2, Lock, Settings, X, Plus, Facebook, Instagram, Music2, Chrome, Youtube, Search,
  Languages, CheckCircle2, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTrialLimits } from "@/hooks/useTrialLimits";
import { TrialLimitBanner } from "@/components/TrialLimitBanner";
import { GoogleConnectDialog } from "@/components/GoogleConnectDialog";
import { TikTokConnectDialog } from "@/components/TikTokConnectDialog";
import { MetaConnectDialog } from "@/components/MetaConnectDialog";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { usePlanUsage } from "@/hooks/usePlanUsage";
import { UpgradePromptDialog } from "@/components/UpgradePromptDialog";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { fireWebhook } from "@/lib/webhooks";

export const Route = createFileRoute("/_app/campaigns/new")({
  component: NewCampaignPage,
  head: () => ({
    meta: [
      { title: "New AI Campaign · AdKavi" },
      { name: "description", content: "Launch your AI campaign in 6 simple steps." },
    ],
  }),
});

// ----- Constants -----

type PlatformKey = "instagram" | "facebook" | "tiktok" | "google" | "youtube";

const PLATFORMS: Array<{
  id: PlatformKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Underlying connection bucket */
  connection: "meta" | "google" | "tiktok";
  tier: 1 | 2 | 3;
}> = [
  { id: "instagram", label: "Instagram", icon: Instagram, connection: "meta", tier: 1 },
  { id: "facebook", label: "Facebook", icon: Facebook, connection: "meta", tier: 1 },
  { id: "tiktok", label: "TikTok", icon: Music2, connection: "tiktok", tier: 2 },
  { id: "google", label: "Google Search & Display", icon: Chrome, connection: "google", tier: 2 },
  { id: "youtube", label: "YouTube", icon: Youtube, connection: "google", tier: 3 },
];

const PRICE_ID_TIER: Record<string, 1 | 2 | 3> = {
  starter_monthly: 1,
  growth_monthly: 2,
  growth_ai_monthly: 3,
  agency_starter_monthly: 1,
  agency_growth_monthly: 2,
  agency_pro_video_monthly: 3,
};

const INDUSTRIES = [
  "E-commerce", "Real Estate", "Health & Wellness", "Beauty & Cosmetics",
  "Legal Services", "Financial Services", "Coaching & Consulting", "Restaurants & Food",
  "Fitness & Gyms", "Home Services", "Automotive", "Education", "Travel & Hospitality",
  "Technology / SaaS", "Local Services", "Non-profit", "Entertainment", "Other",
];

type GoalId =
  | "followers" | "views" | "leads" | "sales" | "whatsapp" | "calls" | "event" | "traffic";

const GOAL_CARDS: Array<{
  id: GoalId;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  defaultCta: string;
}> = [
  { id: "followers", icon: Rocket, title: "Grow Followers", description: "Build your audience and brand presence.", defaultCta: "Learn More" },
  { id: "views", icon: Eye, title: "Get More Views", description: "Maximize video views and reach.", defaultCta: "Watch More" },
  { id: "leads", icon: ClipboardList, title: "Get More Leads", description: "Collect contact info from interested people.", defaultCta: "Sign Up" },
  { id: "sales", icon: ShoppingCart, title: "Drive More Sales", description: "Send people to your store or checkout.", defaultCta: "Shop Now" },
  { id: "whatsapp", icon: MessageCircle, title: "Get WhatsApp Messages", description: "Start conversations on WhatsApp.", defaultCta: "Send Message" },
  { id: "calls", icon: Phone, title: "Get Phone Calls", description: "People tap your ad and call you directly.", defaultCta: "Contact Us" },
  { id: "event", icon: Calendar, title: "Promote an Event", description: "Drive RSVPs or ticket sales.", defaultCta: "Book Now" },
  { id: "traffic", icon: Globe, title: "Drive Website Traffic", description: "Send visitors to your website.", defaultCta: "Learn More" },
];

const CTA_OPTIONS = [
  "Shop Now", "Learn More", "Sign Up", "Contact Us",
  "Send Message", "Get Quote", "Book Now", "Watch More",
] as const;

const LANGUAGES = ["English", "Spanish", "French", "Portuguese", "German", "Italian", "Arabic", "Chinese"];

const RADIUS_OPTIONS = [
  { value: "0", label: "No radius" },
  { value: "10", label: "10 miles" },
  { value: "25", label: "25 miles" },
  { value: "50", label: "50 miles" },
  { value: "100", label: "100 miles" },
];

type CreativeMode = "upload" | "ai" | null;

// ----- Component -----

function NewCampaignPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { isLocked, activeClientId } = useTrialLimits();
  const { plan: userPlan, isAdmin, isTrialing, requiredPlanFor } = usePlanAccess();
  const { reached: usageReached, limit: usageLimit, refresh: refreshUsage } = usePlanUsage();
  const [planUpgradeOpen, setPlanUpgradeOpen] = useState(false);
  const userTier: 1 | 2 | 3 = isAdmin || isTrialing
    ? 3
    : (userPlan?.priceId && PRICE_ID_TIER[userPlan.priceId]) || 1;
  const lockedNew = isLocked("campaign");

  // Stepper
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);
  const goTo = (s: number) => { setStepError(null); setStep(Math.max(1, Math.min(7, s))); };

  // ===== Step 1: Accounts + Platforms =====
  const [metaAccount, setMetaAccount] = useState<string | null>(null);
  const [metaEmail, setMetaEmail] = useState<string>("");
  const [googleAccount, setGoogleAccount] = useState<string | null>(null);
  const [tiktokAccount, setTiktokAccount] = useState<string | null>(null);
  const [connsLoading, setConnsLoading] = useState(true);
  const [showMetaConnect, setShowMetaConnect] = useState(false);
  const [showGoogleConnect, setShowGoogleConnect] = useState(false);
  const [showTikTokConnect, setShowTikTokConnect] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformKey[]>([]);
  const [budget, setBudget] = useState("500");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setConnsLoading(true);
    Promise.all([
      supabase.from("meta_connections").select("account_name").eq("user_id", user.id).maybeSingle(),
      supabase.from("google_connections").select("account_name").eq("user_id", user.id).maybeSingle(),
      supabase.from("tiktok_connections").select("account_name").eq("user_id", user.id).maybeSingle(),
      supabase.from("profiles").select("meta_email").eq("user_id", user.id).maybeSingle(),
    ]).then(([m, g, t, p]) => {
      if (cancelled) return;
      setMetaAccount(m.data?.account_name ?? null);
      setGoogleAccount(g.data?.account_name ?? null);
      setTiktokAccount(t.data?.account_name ?? null);
      setMetaEmail(p.data?.meta_email ?? "");
      setConnsLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  const isPlatformConnected = (p: PlatformKey): boolean => {
    const conn = PLATFORMS.find((x) => x.id === p)!.connection;
    if (conn === "meta") return !!metaAccount;
    if (conn === "google") return !!googleAccount;
    return !!tiktokAccount;
  };
  const anyConnected = !!metaAccount || !!googleAccount || !!tiktokAccount;

  const togglePlatform = (p: PlatformKey) => {
    const opt = PLATFORMS.find((x) => x.id === p)!;
    if (opt.tier > userTier) {
      toast.error(t("wizard.toast.platformLocked"), {
        action: { label: t("wizard.toast.upgrade"), onClick: () => navigate({ to: "/billing/plans" }) },
      });
      return;
    }
    if (!isPlatformConnected(p)) {
      const conn = opt.connection;
      if (conn === "meta") setShowMetaConnect(true);
      else if (conn === "google") setShowGoogleConnect(true);
      else setShowTikTokConnect(true);
      return;
    }
    setSelectedPlatforms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  };

  const canNext1 = selectedPlatforms.length > 0;

  // ===== Step 4 (NEW): Budget & Ad Account Setup =====
  const [budgetType, setBudgetType] = useState<"daily" | "total">("daily");
  const [dailyBudget, setDailyBudget] = useState("20");
  const [totalBudget, setTotalBudget] = useState("500");
  const [campaignDays, setCampaignDays] = useState("14");
  const computedTotalSpend = budgetType === "daily"
    ? Number(dailyBudget || 0) * Number(campaignDays || 0)
    : Number(totalBudget || 0);
  // Rough industry benchmark: ~$8 CPM blended → reach = spend / 8 * 1000
  const estimatedReach = Math.max(0, Math.round((computedTotalSpend / 8) * 1000));

  // Platform minimum daily budgets (USD)
  const PLATFORM_MIN_DAILY: Record<PlatformKey, number> = {
    instagram: 1, facebook: 1, tiktok: 20, google: 1, youtube: 10,
  };
  const effectiveDailySpend = budgetType === "daily"
    ? Number(dailyBudget || 0)
    : Number(campaignDays) > 0 ? Number(totalBudget || 0) / Number(campaignDays) : 0;
  const budgetWarnings = selectedPlatforms
    .map((p) => {
      const min = PLATFORM_MIN_DAILY[p];
      if (effectiveDailySpend < min) {
        const label = PLATFORMS.find((x) => x.id === p)!.label;
        return { platform: label, min };
      }
      return null;
    })
    .filter((x): x is { platform: string; min: number } => x !== null);
  const canNext4Budget =
    Number(campaignDays) > 0 &&
    (budgetType === "daily" ? Number(dailyBudget) > 0 : Number(totalBudget) > 0) &&
    budgetWarnings.length === 0;
  useEffect(() => {
    setBudget(String(computedTotalSpend || 0));
  }, [computedTotalSpend]);

  // ===== Step 2: Business =====
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState<string>("");
  const [promoting, setPromoting] = useState("");
  const canNext2 = businessName.trim().length > 1 && !!industry && promoting.trim().length > 2;

  // ===== Step 3: Goal =====
  const [goalId, setGoalId] = useState<GoalId | null>(null);
  const selectedGoal = goalId ? GOAL_CARDS.find((g) => g.id === goalId)! : null;
  const canNext3 = !!goalId;

  // Optional competitor research (Step 3)
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [competitorInput, setCompetitorInput] = useState("");
  const [competitors, setCompetitors] = useState<string[]>([]);
  const addCompetitor = () => {
    const v = competitorInput.trim();
    if (!v) {
      toast.error(t("competitors.empty"));
      return;
    }
    if (v.length > 80) {
      toast.error(t("competitors.tooLong"));
      return;
    }
    // If it looks like a URL (contains a dot or starts with http), validate it
    if (/^https?:\/\//i.test(v) || v.includes(".")) {
      try {
        const candidate = /^https?:\/\//i.test(v) ? v : `https://${v}`;
        // Throws on invalid URL; also require a TLD-like host
        const u = new URL(candidate);
        if (!u.hostname.includes(".")) throw new Error("no tld");
      } catch {
        toast.error(t("competitors.invalidUrl"));
        return;
      }
    }
    if (competitors.some((c) => c.toLowerCase() === v.toLowerCase())) {
      toast.error(t("competitors.duplicate"));
      return;
    }
    setCompetitors([...competitors, v]);
    setCompetitorInput("");
  };
  const removeCompetitor = (v: string) => setCompetitors(competitors.filter((x) => x !== v));

  // ===== Step 4: Creative =====
  const [creativeMode, setCreativeMode] = useState<CreativeMode>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [aiSubMode, setAiSubMode] = useState<"flyer" | "avatar" | "enhance" | null>(null);
  const [youtubeVideoUrl, setYoutubeVideoUrl] = useState("");
  const canNext4 = (creativeMode === "upload" && !!uploadedFileName) || (creativeMode === "ai" && !!aiSubMode);

  const youtubeVideoId = (() => {
    const url = youtubeVideoUrl.trim();
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname === "youtu.be" || u.hostname === "www.youtu.be") {
        const id = u.pathname.slice(1).split("?")[0];
        return id.length === 11 ? id : null;
      }
      if (u.hostname.includes("youtube.com")) {
        const v = u.searchParams.get("v");
        if (v && v.length === 11) return v;
        const parts = u.pathname.split("/").filter(Boolean);
        const idx = parts.findIndex((p) => ["embed", "shorts", "v", "e"].includes(p));
        if (idx !== -1 && parts[idx + 1]?.length === 11) return parts[idx + 1];
      }
    } catch { /* not a valid URL */ }
    // Accept bare 11-char video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
    return null;
  })();

  // ===== Step 5: AI-generated copy + targeting =====
  const [generating, setGenerating] = useState(false);
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [cta, setCta] = useState<string>("Learn More");

  // Locations
  const [locations, setLocations] = useState<string[]>([]);
  const [locationInput, setLocationInput] = useState("");
  const [radius, setRadius] = useState<string>("0");

  // Audience
  const [ageRange, setAgeRange] = useState<[number, number]>([18, 65]);
  const [gender, setGender] = useState<"all" | "men" | "women">("all");
  const [interests, setInterests] = useState<string[]>([]);
  const [interestInput, setInterestInput] = useState("");
  const [language, setLanguage] = useState<string>("English");
  const [confirmedReview, setConfirmedReview] = useState(false);

  const addLocation = () => {
    const v = locationInput.trim();
    if (!v) return;
    if (!locations.includes(v)) setLocations([...locations, v]);
    setLocationInput("");
  };
  const removeLocation = (v: string) => setLocations(locations.filter((x) => x !== v));
  const addInterest = () => {
    const v = interestInput.trim();
    if (!v) return;
    if (!interests.includes(v)) setInterests([...interests, v]);
    setInterestInput("");
  };
  const removeInterest = (v: string) => setInterests(interests.filter((x) => x !== v));

  const generateAIPlan = async () => {
    if (!selectedGoal) return;
    setGenerating(true);
    try {
      const platformLabel = selectedPlatforms.map((p) => PLATFORMS.find((x) => x.id === p)!.label).join(", ");
      const langHint = adLang === "Spanish"
        ? " IMPORTANT: Write ALL ad copy in Spanish (Latin American Spanish)."
        : adLang === "Both"
          ? " Write ad copy in English. We will translate to Spanish separately."
          : "";

      const { data, error } = await supabase.functions.invoke("generate-campaign", {
        body: {
          product: `${businessName} — ${promoting}`,
          goal: goalId,
          audience: `Industry: ${industry}. Promoting: ${promoting}.${audienceDescription ? ` Audience: ${audienceDescription}.` : ""}`,
          platform: platformLabel,
          budget,
          extraContext: `Selected goal: ${selectedGoal.title}. Creative mode: ${creativeMode === "ai" ? `AI ${aiSubMode}` : "User upload"}.${langHint}`,
          preferredCta: selectedGoal.defaultCta,
        },
      });
      if (error) { toast.error(t("wizard.toast.generationFailed"), { description: error.message }); return; }
      if (data?.error) { toast.error(data.error); return; }
      const c = data.plan?.creatives?.[0];
      if (c) {
        if (adLang === "Spanish") {
          setHeadlineEs(c.headline ?? "");
          setDescriptionEs(c.primary_text ?? c.description ?? "");
          setCta(CTA_OPTIONS.includes(c.cta) ? c.cta : selectedGoal.defaultCta);
        } else {
          setHeadline(c.headline ?? "");
          setDescription(c.primary_text ?? c.description ?? "");
          setCta(CTA_OPTIONS.includes(c.cta) ? c.cta : selectedGoal.defaultCta);
        }
      } else {
        setCta(selectedGoal.defaultCta);
      }
      // Pre-fill interests from AI targeting if available
      const aiInterests: string[] = data.plan?.targeting?.interests ?? [];
      if (aiInterests.length && interests.length === 0) {
        setInterests(aiInterests.slice(0, 8));
      }
      const aiLocs: string[] = data.plan?.targeting?.demographics?.locations ?? [];
      if (aiLocs.length && locations.length === 0) {
        setLocations(aiLocs.slice(0, 5));
      }
    } catch (e) {
      toast.error(t("wizard.toast.generationFailed"), { description: e instanceof Error ? e.message : undefined });
    } finally {
      setGenerating(false);
    }
  };

  // Trigger AI generation when entering Step 5 the first time
  useEffect(() => {
    if (step === 6 && !headline && !generating) {
      generateAIPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ===== Bilingual =====
  const [adLang, setAdLang] = useState<"English" | "Spanish" | "Both">("English");
  const [headlineEs, setHeadlineEs] = useState("");
  const [descriptionEs, setDescriptionEs] = useState("");

  // ===== Audience description (plain English → Meta targeting) =====
  const [audienceDescription, setAudienceDescription] = useState("");
  const [aiTargeting, setAiTargeting] = useState<Record<string, unknown> | null>(null);
  const [translatingAudience, setTranslatingAudience] = useState(false);

  const translateAudience = async () => {
    if (!audienceDescription.trim()) return;
    setTranslatingAudience(true);
    try {
      const { data, error } = await supabase.functions.invoke("translate-audience", {
        body: { description: audienceDescription, language: adLang === "Spanish" ? "Spanish" : "English" },
      });
      if (error || data?.error) { toast.error(t("wizard.toast.translationFailed"), { description: data?.error ?? error?.message }); return; }
      setAiTargeting(data.targeting);
      if (data.targeting?.age_min !== undefined) {
        setAgeRange([data.targeting.age_min, data.targeting.age_max ?? 65]);
      }
      const aiInterests: Array<{ name?: string }> = data.targeting?.interests ?? [];
      if (aiInterests.length) setInterests(aiInterests.map((i) => i.name ?? String(i)).filter(Boolean).slice(0, 8));
      toast.success(t("wizard.step6.audience.translated"));
    } catch (e) {
      toast.error(t("wizard.toast.translationFailed"), { description: e instanceof Error ? e.message : undefined });
    } finally {
      setTranslatingAudience(false);
    }
  };

  const canNext5 =
    headline.trim().length > 0 &&
    description.trim().length > 0 &&
    !!cta &&
    locations.length > 0 &&
    confirmedReview;

  // ===== Step 7: Launch =====
  const [launching, setLaunching] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [launched, setLaunched] = useState(false);
  const [launchedCampaignId, setLaunchedCampaignId] = useState<string | null>(null);
  const [launchedMetaCampaignId, setLaunchedMetaCampaignId] = useState<string | null>(null);
  const [launchedTikTokCampaignId, setLaunchedTikTokCampaignId] = useState<string | null>(null);
  const [launchedGoogleCampaignId, setLaunchedGoogleCampaignId] = useState<string | null>(null);
  const [launchedYouTubeCampaignId, setLaunchedYouTubeCampaignId] = useState<string | null>(null);

  const launch = async () => {
    if (!user) return;
    if (lockedNew) {
      toast.error(t("wizard.toast.trialLimit.title"), { description: t("wizard.toast.trialLimit.desc") });
      return;
    }
    if (usageReached("campaigns")) {
      setPlanUpgradeOpen(true);
      return;
    }
    setLaunching(true);
    try {
      const primaryConn = PLATFORMS.find((p) => p.id === selectedPlatforms[0])!.connection;
      const isMetaCampaign = selectedPlatforms.some((p) => PLATFORMS.find((x) => x.id === p)!.connection === "meta");
      const campaignName = `${businessName} — ${selectedGoal?.title ?? "Campaign"}`;
      const scheduledIso = scheduleMode === "later" && scheduledAt ? new Date(scheduledAt).toISOString() : null;

      const { data: inserted, error } = await supabase.from("campaigns").insert({
        user_id: user.id,
        client_id: activeClientId ?? null,
        name: campaignName,
        platform: primaryConn,
        status: isMetaCampaign && metaAccount ? "draft" : "active",
        budget_type: budgetType,
        daily_budget: budgetType === "daily" ? Number(dailyBudget) || 0 : null,
        total_budget: computedTotalSpend || 0,
        duration_days: Number(campaignDays) || 0,
        headline,
        description,
        cta,
        language: adLang,
        ad_copy_en: { headline, description, cta },
        ad_copy_es: headlineEs ? { headline: headlineEs, description: descriptionEs, cta } : null,
        audience_description: audienceDescription || null,
        targeting_json: aiTargeting ?? null,
        scheduled_at: scheduledIso,
      } as any).select("id").maybeSingle();
      if (error) { toast.error(t("wizard.toast.saveFailed"), { description: error.message }); return; }

      const campaignDbId = inserted?.id;

      if (isMetaCampaign && metaAccount && campaignDbId) {
        const activeHeadline = adLang === "Spanish" ? (headlineEs || headline) : headline;
        const activeDesc = adLang === "Spanish" ? (descriptionEs || description) : description;
        const metaTargeting = aiTargeting ?? {
          age_min: ageRange[0],
          age_max: ageRange[1],
          geo_locations: locations.length > 0 ? { countries: locations } : { countries: ["US"] },
          ...(adLang === "Spanish" || adLang === "Both" ? { locales: [6] } : {}),
        };

        const { data: launchData, error: launchErr } = await supabase.functions.invoke("launch-meta-campaign", {
          body: {
            campaign_id: campaignDbId,
            name: campaignName,
            goal: goalId,
            headline: activeHeadline,
            primary_text: activeDesc,
            cta,
            daily_budget_cents: budgetType === "daily" ? Math.round(Number(dailyBudget) * 100) : undefined,
            total_budget_cents: budgetType === "total" ? Math.round(computedTotalSpend * 100) : undefined,
            budget_type: budgetType,
            duration_days: Number(campaignDays),
            targeting: metaTargeting,
            scheduled_start: scheduledIso ?? undefined,
            language: adLang,
          },
        });

        if (launchErr || launchData?.error) {
          const errMsg = launchData?.error ?? launchErr?.message ?? "Launch failed";
          await supabase.from("campaigns").update({ status: "error", launch_error: errMsg } as any).eq("id", campaignDbId);
          toast.error(t("wizard.toast.launchFailed"), { description: errMsg });
          return;
        }
        setLaunchedMetaCampaignId(launchData?.meta_campaign_id ?? null);
      }

      const isTikTokCampaign = selectedPlatforms.some((p) => PLATFORMS.find((x) => x.id === p)!.connection === "tiktok");
      if (isTikTokCampaign && tiktokAccount && campaignDbId) {
        // Resolve creative from AI Studio handoff if available
        let pendingImageUrl: string | undefined;
        let pendingVideoId: string | undefined;
        try {
          const raw = window.localStorage.getItem("adkavi.pendingCreative");
          if (raw) {
            const pc = JSON.parse(raw) as { image_url?: string; video_id?: string };
            pendingImageUrl = pc.image_url;
            pendingVideoId = pc.video_id;
          }
        } catch { /* ignore */ }

        const activeHeadline = adLang === "Spanish" ? (headlineEs || headline) : headline;
        const activeDesc = adLang === "Spanish" ? (descriptionEs || description) : description;

        const { data: tikTokLaunchData, error: tikTokLaunchErr } = await supabase.functions.invoke("launch-tiktok-campaign", {
          body: {
            campaign_id: campaignDbId,
            name: campaignName,
            goal: goalId,
            headline: activeHeadline,
            primary_text: activeDesc,
            cta,
            image_url: pendingImageUrl,
            video_id: pendingVideoId,
            daily_budget: budgetType === "daily" ? Number(dailyBudget) || 20 : undefined,
            total_budget: budgetType === "total" ? computedTotalSpend || undefined : undefined,
            budget_type: budgetType,
            duration_days: Number(campaignDays),
            age_min: ageRange[0],
            age_max: ageRange[1],
            locations: locations.length > 0 ? locations : ["US"],
            scheduled_start: scheduledIso ?? undefined,
            language: adLang,
          },
        });

        if (tikTokLaunchErr || tikTokLaunchData?.error) {
          const errMsg = tikTokLaunchData?.error ?? tikTokLaunchErr?.message ?? "Launch failed";
          await supabase.from("campaigns").update({ status: "error", launch_error: errMsg } as any).eq("id", campaignDbId);
          toast.error(t("wizard.toast.launchFailed"), { description: errMsg });
          return;
        }
        setLaunchedTikTokCampaignId(tikTokLaunchData?.tiktok_campaign_id ?? null);
      }

      const googleLaunchBase = {
        campaign_id: campaignDbId,
        name: campaignName,
        goal: goalId,
        headline: adLang === "Spanish" ? (headlineEs || headline) : headline,
        primary_text: adLang === "Spanish" ? (descriptionEs || description) : description,
        cta,
        daily_budget: budgetType === "daily" ? Number(dailyBudget) || 20 : undefined,
        total_budget: budgetType === "total" ? computedTotalSpend || undefined : undefined,
        budget_type: budgetType,
        duration_days: Number(campaignDays),
        locations: locations.length > 0 ? locations : ["US"],
        language: adLang,
        business_name: businessName,
        scheduled_start: scheduledIso ?? undefined,
      };

      const hasGoogleSearch = selectedPlatforms.includes("google");
      if (hasGoogleSearch && googleAccount && campaignDbId) {
        const { data: googleLaunchData, error: googleLaunchErr } = await supabase.functions.invoke("launch-google-campaign", {
          body: { ...googleLaunchBase, is_youtube: false },
        });
        if (googleLaunchErr || googleLaunchData?.error) {
          const errMsg = googleLaunchData?.error ?? googleLaunchErr?.message ?? "Launch failed";
          await supabase.from("campaigns").update({ status: "error", launch_error: errMsg } as any).eq("id", campaignDbId);
          toast.error(t("wizard.toast.launchFailed"), { description: errMsg });
          return;
        }
        setLaunchedGoogleCampaignId(googleLaunchData?.google_campaign_id ?? null);
      }

      const hasYouTube = selectedPlatforms.includes("youtube");
      if (hasYouTube && googleAccount && campaignDbId) {
        const { data: ytLaunchData, error: ytLaunchErr } = await supabase.functions.invoke("launch-google-campaign", {
          body: {
            ...googleLaunchBase,
            is_youtube: true,
            ...(youtubeVideoId ? { youtube_video_id: youtubeVideoId } : {}),
          },
        });
        if (ytLaunchErr || ytLaunchData?.error) {
          const errMsg = ytLaunchData?.error ?? ytLaunchErr?.message ?? "Launch failed";
          await supabase.from("campaigns").update({ status: "error", launch_error: errMsg } as any).eq("id", campaignDbId);
          toast.error(t("wizard.toast.launchFailed"), { description: errMsg });
          return;
        }
        setLaunchedYouTubeCampaignId(ytLaunchData?.youtube_campaign_id ?? null);
      }

      setLaunchedCampaignId(campaignDbId ?? null);

      try {
        const { markOnboardingStep } = await import("@/components/OnboardingChecklist");
        markOnboardingStep(user.id, "campaign");
        if ((Number(dailyBudget) || 0) > 0 || (computedTotalSpend || 0) > 0) markOnboardingStep(user.id, "budget");
        markOnboardingStep(user.id, "launch");
      } catch { /* ignore */ }

      fireWebhook("campaign.launched", {
        campaign_id: campaignDbId,
        name: campaignName,
        platform: primaryConn,
        budget_type: budgetType,
        daily_budget: budgetType === "daily" ? Number(dailyBudget) || 0 : null,
        total_budget: computedTotalSpend || 0,
        duration_days: Number(campaignDays) || 0,
      });

      void refreshUsage();
      setLaunched(true);
    } finally {
      setLaunching(false);
    }
  };

  if (!user) return null;

  const stepLabels = [
    t("wizard.step.accounts"),
    t("wizard.step.business"),
    t("wizard.step.goal"),
    t("wizard.step.budget"),
    t("wizard.step.creative"),
    t("wizard.step.aiBuild"),
    t("wizard.step.review"),
  ];
  const hasTikTokPlatform = selectedPlatforms.some((p) => PLATFORMS.find((x) => x.id === p)!.connection === "tiktok");

  const validateAndAdvance = (toStep: number) => {
    let err: string | null = null;
    if (step === 1 && !canNext1) err = t("wizard.error.platforms");
    else if (step === 2) {
      if (businessName.trim().length < 2) err = t("wizard.error.businessName");
      else if (!industry) err = t("wizard.error.industry");
      else if (promoting.trim().length < 3) err = t("wizard.error.promoting");
    } else if (step === 3 && !canNext3) err = t("wizard.error.goal");
    else if (step === 5) {
      if (!creativeMode) err = t("wizard.error.creative");
      else if (creativeMode === "upload" && !uploadedFileName) err = t("wizard.error.creativeUpload");
      else if (creativeMode === "ai" && !aiSubMode) err = t("wizard.error.creativeAi");
    }
    if (err) {
      setStepError(err);
      toast.error(err);
      return;
    }
    goTo(toStep);
  };

  const StepError = () =>
    stepError ? (
      <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        {stepError}
      </div>
    ) : null;

  return (
    <div className="min-h-full">
      <div className="mx-auto max-w-5xl px-4 pt-4">
        <Link to="/campaigns" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("wizard.backToList")}
        </Link>
      </div>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <TrialLimitBanner kind="campaign" />

        {/* ===== POST-LAUNCH SUCCESS SCREEN ===== */}
        {launched && (
          <div className="space-y-6">
            <div className="text-center space-y-3">
              <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-500/15">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold">
                {scheduleMode === "later" ? t("wizard.success.scheduledTitle") : t("wizard.success.liveTitle")}
              </h2>
              <p className="text-muted-foreground">
                {scheduleMode === "later"
                  ? t("wizard.success.scheduledDesc").replace("{date}", new Date(scheduledAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }))
                  : t("wizard.success.liveDesc")}
              </p>
            </div>

            <Card>
              <CardContent className="pt-6 space-y-3">
                <SummaryRow label={t("wizard.success.label.name")} value={`${businessName} — ${goalId ? t(`wizard.goal.${goalId}.title`) : t("health.campaign")}`} />
                <SummaryRow
                  label={t("wizard.success.label.platform")}
                  value={selectedPlatforms.map((p) => PLATFORMS.find((x) => x.id === p)!.label).join(", ")}
                />
                <SummaryRow
                  label={t("wizard.success.label.budget")}
                  value={budgetType === "daily"
                    ? t("wizard.budget.daily").replace("${daily}", Number(dailyBudget).toLocaleString()).replace("{days}", campaignDays)
                    : t("wizard.budget.total").replace("${total}", computedTotalSpend.toLocaleString())}
                />
                <SummaryRow label={t("wizard.success.label.duration")} value={t("wizard.success.label.duration.value").replace("{n}", campaignDays)} />
                {launchedMetaCampaignId && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.success.metaCampaignId")}</p>
                    <p className="mt-0.5 text-sm font-mono text-muted-foreground">{launchedMetaCampaignId}</p>
                  </div>
                )}
                {launchedGoogleCampaignId && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.success.googleCampaignId")}</p>
                    <p className="mt-0.5 text-sm font-mono text-muted-foreground">{launchedGoogleCampaignId}</p>
                  </div>
                )}
                {launchedYouTubeCampaignId && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.success.youtubeCampaignId")}</p>
                    <p className="mt-0.5 text-sm font-mono text-muted-foreground">{launchedYouTubeCampaignId}</p>
                    {!youtubeVideoId && (
                      <p className="mt-0.5 text-xs text-amber-400">Add your video creative in Google Ads Manager to go live.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TikTok upsell — only if they didn't already include TikTok and have no TikTok account */}
            {!hasTikTokPlatform && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 flex items-center justify-center rounded-full bg-foreground/10 shrink-0">
                      <Music2 className="h-5 w-5 text-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{t("wizard.success.tiktokUpsell.title")}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t("wizard.success.tiktokUpsell.desc")}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" onClick={() => navigate({ to: "/campaigns/new" })}>
                          <Music2 className="mr-2 h-4 w-4" /> {t("wizard.success.tiktokUpsell.btn")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate({ to: "/campaigns" })}>
                          {t("wizard.success.tiktokUpsell.later")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => navigate({ to: "/campaigns" })}>
                {t("wizard.success.viewAll")}
              </Button>
              <Button onClick={() => navigate({ to: "/dashboard" })} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                {t("wizard.success.dashboard")}
              </Button>
            </div>
          </div>
        )}

        {!launched && (
        <>
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10">
            <Sparkles className="h-6 w-6 text-orange-400" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">
              {t("wizard.header.title")}
            </span>
          </h1>
          <p className="mt-2 text-muted-foreground">{t("wizard.header.subtitle")}</p>
        </div>

        {/* Stepper */}
        <div className="mb-10 flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
          {[1, 2, 3, 4, 5, 6, 7].map((s, idx) => (
            <div key={s} className="flex items-center gap-1 sm:gap-2">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-medium ${
                    step >= s
                      ? "border-orange-500 bg-orange-500 text-white"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                <span className={`hidden sm:block text-[10px] uppercase tracking-wider ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>
                  {stepLabels[idx]}
                </span>
              </div>
              {s < 7 && <div className={`h-px w-6 sm:w-8 ${step > s ? "bg-orange-500" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* ===== STEP 1: Accounts ===== */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("wizard.step1.title")}</CardTitle>
              <CardDescription>{t("wizard.step1.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {connsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("wizard.step1.loading")}
                </div>
              ) : !anyConnected ? (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                  <p className="font-medium text-foreground">{t("wizard.step1.noAccounts")}</p>
                  <p className="mt-1 text-muted-foreground">
                    {t("wizard.step1.settingsLinkPre")}{" "}
                    <Link to="/settings" className="text-primary underline underline-offset-2">
                      Settings › Integrations
                    </Link>{" "}
                    {t("wizard.step1.settingsLinkPost")}
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="mb-2 block">{t("wizard.step1.platformsLabel")}</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {PLATFORMS.map((opt) => {
                      const Icon = opt.icon;
                      const locked = opt.tier > userTier;
                      const connected = isPlatformConnected(opt.id);
                      const selected = selectedPlatforms.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => togglePlatform(opt.id)}
                          className={`flex items-center justify-between rounded-lg border-2 px-4 py-3 text-left text-sm transition-all ${
                            selected
                              ? "border-primary bg-primary/5"
                              : "border-border bg-card hover:border-primary/50"
                          } ${locked ? "opacity-60" : ""}`}
                        >
                          <span className="flex items-center gap-2 font-medium">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            {opt.label}
                          </span>
                          <span className="flex items-center gap-2">
                            {!connected && !locked && (
                              <Badge variant="outline" className="text-[10px]">{t("wizard.step1.connectBadge")}</Badge>
                            )}
                            {locked ? (
                              <Lock className="h-4 w-4 text-muted-foreground" />
                            ) : selected ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {t("wizard.step1.tip")}
                  </p>
                </div>
              )}

              <StepError />
              <div className="flex items-center justify-between">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/settings"><Settings className="mr-2 h-4 w-4" /> {t("wizard.step1.manageIntegrations")}</Link>
                </Button>
                <Button onClick={() => validateAndAdvance(2)} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                  {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== STEP 2: Business ===== */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("wizard.step2.title")}</CardTitle>
              <CardDescription>{t("wizard.step2.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="biz-name">{t("wizard.step2.bizName")}</Label>
                <Input
                  id="biz-name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={t("wizard.step2.bizNamePlaceholder")}
                />
              </div>
              <div>
                <Label>{t("wizard.step2.industry")}</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger><SelectValue placeholder={t("wizard.step2.industryPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="promoting">{t("wizard.step2.promoting")}</Label>
                <Textarea
                  id="promoting"
                  value={promoting}
                  onChange={(e) => setPromoting(e.target.value)}
                  placeholder={t("wizard.step2.promotingPlaceholder")}
                  rows={3}
                />
              </div>
              <StepError />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => goTo(1)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
                <Button onClick={() => validateAndAdvance(3)} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                  {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== STEP 3: Goal ===== */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("wizard.step3.title")}</CardTitle>
              <CardDescription>{t("wizard.step3.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {GOAL_CARDS.map((card) => {
                  const Icon = card.icon;
                  const isSelected = goalId === card.id;
                  return (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => setGoalId(card.id)}
                      className={`group relative flex flex-col gap-2 rounded-xl border-2 p-4 text-left transition-all hover:border-primary/60 hover:bg-accent/40 ${
                        isSelected
                          ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                          : "border-border bg-card"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isSelected ? "bg-primary/15 text-primary" : "bg-primary/10 text-primary"}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        {isSelected && <Badge className="ml-auto bg-orange-500 text-white border-0">{t("wizard.step3.selectedBadge")}</Badge>}
                      </div>
                      <p className="text-base font-semibold">{t(`wizard.goal.${card.id}.title`)}</p>
                      <p className="text-sm text-muted-foreground">{t(`wizard.goal.${card.id}.desc`)}</p>
                    </button>
                  );
                })}
              </div>

              {/* Optional competitor research */}
              <div className="rounded-xl border border-border bg-card/40 p-4">
                <button
                  type="button"
                  onClick={() => setShowCompetitors((v) => !v)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">{t("wizard.step3.competitors.toggle")} <span className="text-muted-foreground font-normal">{t("wizard.step3.competitors.optional")}</span></span>
                  </div>
                  <span className="text-xs text-muted-foreground">{showCompetitors ? t("wizard.step3.competitors.hide") : t("wizard.step3.competitors.show")}</span>
                </button>
                {showCompetitors && (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      {t("wizard.step3.competitors.desc")}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("wizard.step3.competitors.placeholder")}
                        value={competitorInput}
                        onChange={(e) => setCompetitorInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompetitor(); } }}
                      />
                      <Button type="button" variant="outline" onClick={addCompetitor}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {competitors.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {competitors.map((c) => {
                          const url = /^https?:\/\//i.test(c)
                            ? c
                            : `https://www.google.com/search?q=${encodeURIComponent(c + " ads")}`;
                          return (
                            <Badge key={c} variant="secondary" className="gap-1.5">
                              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                {c}
                              </a>
                              <button onClick={() => removeCompetitor(c)} className="opacity-60 hover:opacity-100">
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <StepError />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => goTo(2)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
                <Button onClick={() => validateAndAdvance(4)} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                  {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== STEP 4: Budget & Ad Account Setup ===== */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("wizard.step4.title")}</CardTitle>
              <CardDescription>{t("wizard.step4.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">{t("wizard.step4.budgetTypeLabel")}</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setBudgetType("daily")}
                    className={`rounded-xl border-2 p-4 text-left transition ${
                      budgetType === "daily"
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/50"
                    }`}
                  >
                    <p className="font-semibold">{t("wizard.step4.dailyBudget.title")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("wizard.step4.dailyBudget.desc")}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBudgetType("total")}
                    className={`rounded-xl border-2 p-4 text-left transition ${
                      budgetType === "total"
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/50"
                    }`}
                  >
                    <p className="font-semibold">{t("wizard.step4.totalBudget.title")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("wizard.step4.totalBudget.desc")}</p>
                  </button>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {budgetType === "daily" ? (
                  <div>
                    <Label htmlFor="daily-budget">{t("wizard.step4.dailyLabel")}</Label>
                    <Input
                      id="daily-budget"
                      type="number"
                      min={1}
                      value={dailyBudget}
                      onChange={(e) => setDailyBudget(e.target.value)}
                      placeholder="20"
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="total-budget">{t("wizard.step4.totalLabel")}</Label>
                    <Input
                      id="total-budget"
                      type="number"
                      min={1}
                      value={totalBudget}
                      onChange={(e) => setTotalBudget(e.target.value)}
                      placeholder="500"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="campaign-days">{t("wizard.step4.durationLabel")}</Label>
                  <Input
                    id="campaign-days"
                    type="number"
                    min={1}
                    value={campaignDays}
                    onChange={(e) => setCampaignDays(e.target.value)}
                    placeholder="14"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.step4.reach.label")}</p>
                <p className="mt-1 text-2xl font-bold">{t("wizard.step4.reach.value").replace("{n}", estimatedReach.toLocaleString())}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("wizard.step4.reach.desc").replace("${total}", computedTotalSpend.toLocaleString())}
                </p>
                <p className="mt-3 text-xs italic text-muted-foreground border-t border-primary/20 pt-2">
                  {t("wizard.step4.reach.note")}
                </p>
              </div>

              {budgetWarnings.length > 0 && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm">
                  <p className="font-semibold text-destructive">{t("wizard.step4.warning.title")}</p>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {budgetWarnings.map((w) => (
                      <li key={w.platform}>
                        • {t("wizard.step4.warning.item").replace("{platform}", w.platform).replace("${min}", String(w.min)).replace("${spend}", effectiveDailySpend.toFixed(2))}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-muted-foreground">{t("wizard.step4.warning.hint")}</p>
                </div>
              )}

              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                <p className="text-foreground">
                  {t("wizard.step4.notice")}
                </p>
              </div>

              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <p className="font-semibold mb-1">{t("wizard.step4.setup.title")}</p>
                <p className="text-xs text-muted-foreground mb-3">
                  {t("wizard.step4.setup.desc")}
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  <a
                    href="https://business.facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:border-primary/50 transition"
                  >
                    <Facebook className="h-4 w-4" /> {t("wizard.step4.setup.meta")}
                  </a>
                  <a
                    href="https://ads.tiktok.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:border-primary/50 transition"
                  >
                    <Music2 className="h-4 w-4" /> {t("wizard.step4.setup.tiktok")}
                  </a>
                  <a
                    href="https://ads.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:border-primary/50 transition"
                  >
                    <Chrome className="h-4 w-4" /> {t("wizard.step4.setup.google")}
                  </a>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => goTo(3)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
                <Button disabled={!canNext4Budget} onClick={() => goTo(5)} className="bg-orange-500 hover:bg-orange-600 text-white border-0 disabled:opacity-50">
                  {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== STEP 5: Creative ===== */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("wizard.step5.title")}</CardTitle>
              <CardDescription>{t("wizard.step5.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => { setCreativeMode("upload"); setAiSubMode(null); }}
                  className={`flex flex-col gap-2 rounded-xl border-2 p-4 text-left transition ${
                    creativeMode === "upload" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Upload className="h-4 w-4" />
                    </div>
                    <Badge variant="outline">{t("wizard.step5.optionA")}</Badge>
                  </div>
                  <p className="font-semibold">{t("wizard.step5.upload.title")}</p>
                  <p className="text-sm text-muted-foreground">{t("wizard.step5.upload.desc")}</p>
                </button>

                <button
                  type="button"
                  onClick={() => setCreativeMode("ai")}
                  className={`flex flex-col gap-2 rounded-xl border-2 p-4 text-left transition ${
                    creativeMode === "ai" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Wand2 className="h-4 w-4" />
                    </div>
                    <Badge variant="outline">{t("wizard.step5.optionB")}</Badge>
                  </div>
                  <p className="font-semibold">{t("wizard.step5.ai.title")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("wizard.step5.ai.desc")}
                  </p>
                </button>
              </div>

              {creativeMode === "upload" && (
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label htmlFor="upload">{t("wizard.step5.uploadLabel")}</Label>
                  <Input
                    id="upload"
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setUploadedFileName(e.target.files?.[0]?.name ?? "")}
                    className="mt-2"
                  />
                  {uploadedFileName && (
                    <p className="mt-2 text-xs text-muted-foreground">{t("wizard.step5.selectedFile").replace("{name}", uploadedFileName)}</p>
                  )}
                </div>
              )}

              {creativeMode === "ai" && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                  <Label>{t("wizard.step5.aiType")}</Label>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      { id: "flyer", labelKey: "wizard.step5.flyer", to: "/creative" },
                      { id: "avatar", labelKey: "wizard.step5.avatar", to: "/ai-studio" },
                      { id: "enhance", labelKey: "wizard.step5.enhance", to: "/ai-studio" },
                    ].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setAiSubMode(o.id as "flyer" | "avatar" | "enhance")}
                        className={`rounded-md border px-3 py-2 text-sm transition ${
                          aiSubMode === o.id
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {t(o.labelKey)}
                      </button>
                    ))}
                  </div>
                  {aiSubMode && (
                    <p className="text-xs text-muted-foreground">
                      {t("wizard.step5.aiHintPre")}{" "}
                      <Link to={aiSubMode === "flyer" ? "/creative" : "/ai-studio"} className="text-primary underline underline-offset-2">
                        AI Studio
                      </Link>{" "}
                      {t("wizard.step5.aiHintPost")}
                    </p>
                  )}
                </div>
              )}

              {selectedPlatforms.includes("youtube") && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Youtube className="h-4 w-4 text-red-500" />
                    <Label htmlFor="youtube-video-url" className="text-sm font-semibold">
                      YouTube Video URL
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional — paste to attach creative now)</span>
                    </Label>
                  </div>
                  <Input
                    id="youtube-video-url"
                    type="url"
                    placeholder="https://youtube.com/watch?v=..."
                    value={youtubeVideoUrl}
                    onChange={(e) => setYoutubeVideoUrl(e.target.value)}
                  />
                  {youtubeVideoId ? (
                    <p className="text-xs text-green-400 flex items-center gap-1.5">
                      <Check className="h-3 w-3" /> Video detected — will attach at launch
                    </p>
                  ) : youtubeVideoUrl ? (
                    <p className="text-xs text-amber-400">Couldn't parse a video ID. Try youtube.com/watch?v=… or youtu.be/…</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Leave blank to create the campaign shell now and add your video in Google Ads Manager later.
                    </p>
                  )}
                </div>
              )}

              <StepError />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => goTo(4)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
                <Button onClick={() => validateAndAdvance(6)} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                  {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== STEP 5: AI Build ===== */}
        {step === 6 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("wizard.step6.title")}</CardTitle>
                <CardDescription>{t("wizard.step6.desc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {generating && !headline && (
                  <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 py-10 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("wizard.step6.generating")}
                  </div>
                )}

                {/* Language toggle */}
                <div className="flex items-center gap-2">
                  <Languages className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground mr-2">{t("wizard.step6.adLang")}</span>
                  {(["English", "Spanish", "Both"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setAdLang(l)}
                      className={`rounded-md border px-3 py-1.5 text-sm transition ${
                        adLang === l ? "border-primary bg-primary/10 font-medium" : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {l === "Both" ? "EN + ES" : l === "English" ? t("wizard.step6.english") : t("wizard.step6.spanish")}
                    </button>
                  ))}
                </div>

                {/* Ad Copy */}
                <section className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.step6.adCopy")}</h3>
                    <Button size="sm" variant="ghost" onClick={generateAIPlan} disabled={generating}>
                      {generating ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
                      {t("wizard.step6.regenerate")}
                    </Button>
                  </div>

                  {/* English copy */}
                  {(adLang === "English" || adLang === "Both") && (
                    <div className="space-y-3">
                      {adLang === "Both" && (
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("wizard.step6.english")}</p>
                      )}
                      <div>
                        <Label htmlFor="headline">{t("wizard.step6.headline")}</Label>
                        <Input id="headline" value={headline} onChange={(e) => setHeadline(e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="desc">{t("wizard.step6.description")}</Label>
                        <Textarea id="desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                      </div>
                    </div>
                  )}

                  {/* Spanish copy */}
                  {(adLang === "Spanish" || adLang === "Both") && (
                    <div className="space-y-3">
                      {adLang === "Both" && (
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("wizard.step6.spanish")}</p>
                      )}
                      <div>
                        <Label htmlFor="headline-es">
                          {t("wizard.step6.headlineEs")}
                        </Label>
                        <Input
                          id="headline-es"
                          value={headlineEs}
                          onChange={(e) => setHeadlineEs(e.target.value)}
                          placeholder={headline ? `e.g. ${headline} → traducido` : t("wizard.step6.headlineEsPlaceholder")}
                        />
                      </div>
                      <div>
                        <Label htmlFor="desc-es">
                          {t("wizard.step6.descriptionEs")}
                        </Label>
                        <Textarea
                          id="desc-es"
                          rows={3}
                          value={descriptionEs}
                          onChange={(e) => setDescriptionEs(e.target.value)}
                          placeholder={t("wizard.step6.descriptionEsPlaceholder")}
                        />
                      </div>
                      {(!headlineEs || !descriptionEs) && (
                        <p className="text-xs text-muted-foreground">
                          {t("wizard.step6.esTip")}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label>{t("wizard.step6.cta")}</Label>
                    <Select value={cta} onValueChange={setCta}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CTA_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </section>

                <Separator />

                {/* Target Location */}
                <section className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.step6.location.title")}</h3>
                  <div>
                    <Label>{t("wizard.step6.location.label")}</Label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        placeholder={t("wizard.step6.location.placeholder")}
                        value={locationInput}
                        onChange={(e) => setLocationInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }}
                      />
                      <Button type="button" variant="outline" onClick={addLocation}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {locations.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {locations.map((l) => (
                          <Badge key={l} variant="secondary" className="gap-1">
                            {l}
                            <button onClick={() => removeLocation(l)} className="ml-1 opacity-60 hover:opacity-100">
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>{t("wizard.step6.location.radius")}</Label>
                    <Select value={radius} onValueChange={setRadius}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RADIUS_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </section>

                <Separator />

                {/* Audience description → AI translate */}
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.step6.audience.title")}</h3>
                    <span className="text-xs text-muted-foreground">{t("wizard.step6.audience.optional")}</span>
                  </div>
                  <Textarea
                    rows={2}
                    placeholder={t("wizard.step6.audience.placeholder")}
                    value={audienceDescription}
                    onChange={(e) => setAudienceDescription(e.target.value)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!audienceDescription.trim() || translatingAudience}
                    onClick={translateAudience}
                  >
                    {translatingAudience
                      ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> {t("wizard.step6.audience.translating")}</>
                      : <><Sparkles className="mr-2 h-3.5 w-3.5" /> {t("wizard.step6.audience.translate")}</>
                    }
                  </Button>
                  {aiTargeting && (
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <Check className="h-3 w-3" /> {t("wizard.step6.audience.translated")}
                    </p>
                  )}
                </section>

                <Separator />

                {/* Target Audience */}
                <section className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("wizard.step6.target.title")}</h3>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label>{t("wizard.step6.target.age")}</Label>
                      <span className="text-sm text-muted-foreground">{ageRange[0]} – {ageRange[1] >= 65 ? "65+" : ageRange[1]}</span>
                    </div>
                    <Slider
                      min={13}
                      max={65}
                      step={1}
                      value={ageRange}
                      onValueChange={(v) => setAgeRange([v[0], v[1]] as [number, number])}
                      className="mt-3"
                    />
                  </div>

                  <div>
                    <Label>{t("wizard.step6.target.gender")}</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {(["all", "men", "women"] as const).map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`rounded-md border px-3 py-2 text-sm transition ${
                            gender === g
                              ? "border-primary bg-primary/10"
                              : "border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {g === "all" ? t("wizard.step6.target.genderAll") : g === "men" ? t("wizard.step6.target.genderMen") : t("wizard.step6.target.genderWomen")}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>{t("wizard.step6.target.interests")}</Label>
                    <p className="text-xs text-muted-foreground mb-2">{t("wizard.step6.target.interestsHint")}</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("wizard.step6.target.interestPlaceholder")}
                        value={interestInput}
                        onChange={(e) => setInterestInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(); } }}
                      />
                      <Button type="button" variant="outline" onClick={addInterest}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {interests.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {interests.map((i) => (
                          <Badge key={i} variant="outline" className="gap-1">
                            {i}
                            <button onClick={() => removeInterest(i)} className="ml-1 opacity-60 hover:opacity-100">
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>{t("wizard.step6.target.language")}</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </section>

                <Separator />

                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-border accent-primary"
                    checked={confirmedReview}
                    onChange={(e) => setConfirmedReview(e.target.checked)}
                  />
                  <span>{t("wizard.step6.confirm")}</span>
                </label>

                <div className="flex justify-between">
                  <Button variant="ghost" onClick={() => goTo(5)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
                  <Button disabled={!canNext5} onClick={() => goTo(7)} className="bg-orange-500 hover:bg-orange-600 text-white border-0 disabled:opacity-50">
                    {t("wizard.next")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== STEP 7: Review & Launch ===== */}
        {step === 7 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("wizard.step7.title")}</CardTitle>
                <CardDescription>
                  {t("wizard.step7.desc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <SummaryRow label={t("wizard.step7.label.name")} value={`${businessName} — ${goalId ? t(`wizard.goal.${goalId}.title`) : t("health.campaign")}`} />
                  <SummaryRow label={t("wizard.step7.label.industry")} value={industry} />
                </div>
                <SummaryRow label={t("wizard.step7.label.promoting")} value={promoting} />
                <SummaryRow
                  label={t("wizard.step7.label.platforms")}
                  value={selectedPlatforms.map((p) => PLATFORMS.find((x) => x.id === p)!.label).join(", ")}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <SummaryRow label={t("wizard.step7.label.goal")} value={goalId ? t(`wizard.goal.${goalId}.title`) : "—"} />
                  <SummaryRow
                    label={t("wizard.step7.label.budget")}
                    value={
                      budgetType === "daily"
                        ? t("wizard.budget.dailyFull").replace("${daily}", Number(dailyBudget).toLocaleString()).replace("{days}", campaignDays).replace("${computed}", computedTotalSpend.toLocaleString())
                        : t("wizard.budget.totalFull").replace("${total}", computedTotalSpend.toLocaleString()).replace("{days}", campaignDays)
                    }
                  />
                </div>
                <SummaryRow
                  label={t("wizard.step7.label.creative")}
                  value={creativeMode === "upload" ? `Upload: ${uploadedFileName}` : `AI ${aiSubMode ?? ""}`}
                />
                <SummaryRow
                  label={t("wizard.step7.label.competitors")}
                  value={competitors.length ? competitors.join(", ") : t("wizard.step7.noneAdded")}
                />
                <Separator />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("wizard.step7.adCopy")}</p>
                  {(adLang === "English" || adLang === "Both") && (
                    <div className={adLang === "Both" ? "mb-3" : ""}>
                      {adLang === "Both" && <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">{t("wizard.step6.english")}</p>}
                      <p className="font-semibold">{headline || "—"}</p>
                      <p className="text-sm text-muted-foreground mt-1">{description || "—"}</p>
                    </div>
                  )}
                  {(adLang === "Spanish" || adLang === "Both") && (
                    <div>
                      {adLang === "Both" && <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">{t("wizard.step6.spanish")}</p>}
                      <p className="font-semibold">{headlineEs || headline || "—"}</p>
                      <p className="text-sm text-muted-foreground mt-1">{descriptionEs || description || "—"}</p>
                    </div>
                  )}
                  <Badge variant="outline" className="mt-2">{cta}</Badge>
                </div>
                <Separator />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("wizard.step7.targeting")}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <SummaryRow label={t("wizard.step7.label.locations")} value={locations.join(", ") || "—"} />
                    <SummaryRow label={t("wizard.step7.label.radius")} value={RADIUS_OPTIONS.find((r) => r.value === radius)?.label ?? "—"} />
                    <SummaryRow label={t("wizard.step7.label.age")} value={`${ageRange[0]} – ${ageRange[1] >= 65 ? "65+" : ageRange[1]}`} />
                    <SummaryRow label={t("wizard.step7.label.gender")} value={gender} className="capitalize" />
                    <SummaryRow label={t("wizard.step7.label.interests")} value={interests.join(", ") || "—"} />
                    <SummaryRow label={t("wizard.step7.label.language")} value={adLang} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scheduling */}
            <Card>
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-semibold text-sm">{t("wizard.step7.schedule.title")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("wizard.step7.schedule.desc")}</p>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setScheduleMode("now")}
                    className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left text-sm transition ${
                      scheduleMode === "now" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                    }`}
                  >
                    <Rocket className="h-4 w-4 text-primary shrink-0" />
                    <div>
                      <p className="font-medium">{t("wizard.step7.schedule.now.title")}</p>
                      <p className="text-xs text-muted-foreground">{t("wizard.step7.schedule.now.desc")}</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setScheduleMode("later")}
                    className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left text-sm transition ${
                      scheduleMode === "later" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                    }`}
                  >
                    <Clock className="h-4 w-4 text-primary shrink-0" />
                    <div>
                      <p className="font-medium">{t("wizard.step7.schedule.later.title")}</p>
                      <p className="text-xs text-muted-foreground">{t("wizard.step7.schedule.later.desc")}</p>
                    </div>
                  </button>
                </div>
                {scheduleMode === "later" && (
                  <div className="mt-4">
                    <Label htmlFor="scheduled-at">{t("wizard.step7.schedule.dateLabel")}</Label>
                    <Input
                      id="scheduled-at"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
                      className="mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => goTo(6)}><ArrowLeft className="mr-2 h-4 w-4" /> {t("wizard.back")}</Button>
              <Button
                size="lg"
                onClick={launch}
                disabled={launching || (scheduleMode === "later" && !scheduledAt)}
                className="bg-orange-500 hover:bg-orange-600 text-white border-0 disabled:opacity-50"
              >
                {launching
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("wizard.step7.launching")}</>
                  : scheduleMode === "later"
                    ? <><Clock className="mr-2 h-4 w-4" /> {t("wizard.step7.scheduleCampaign")}</>
                    : <><Rocket className="mr-2 h-4 w-4" /> {t("wizard.step7.launchCampaign")}</>
                }
              </Button>
            </div>
          </div>
        )}

        </> /* end {!launched && } */
        )}

        {/* Connection dialogs */}
        <MetaConnectDialog
          open={showMetaConnect}
          onOpenChange={setShowMetaConnect}
          userId={user.id}
          accountEmail={metaEmail}
          onConnected={(name) => setMetaAccount(name)}
        />
        <GoogleConnectDialog
          open={showGoogleConnect}
          onOpenChange={setShowGoogleConnect}
          userId={user.id}
          onConnected={(name) => setGoogleAccount(name)}
        />
        <TikTokConnectDialog
          open={showTikTokConnect}
          onOpenChange={setShowTikTokConnect}
          userId={user.id}
          onConnected={(name) => setTiktokAccount(name)}
        />
        <UpgradePromptDialog
          open={planUpgradeOpen}
          onOpenChange={setPlanUpgradeOpen}
          title={t("plan.limit.campaignsTitle")}
          requiredPlan={requiredPlanFor("campaigns") === "Launch" ? "Scale" : requiredPlanFor("campaigns")}
          description={
            usageLimit("campaigns") !== null
              ? t("plan.limit.campaignsDesc", { limit: String(usageLimit("campaigns")) })
              : undefined
          }
        />
      </main>
    </div>
  );
}

function SummaryRow({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm ${className ?? ""}`}>{value || "—"}</p>
    </div>
  );
}
